import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  Loader2, 
  BarChart3,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Package,
  Users,
  Truck,
  RefreshCw,
  Download,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  MapPin
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('fleetflow_token')}`,
  'Content-Type': 'application/json'
});

// Component extracted to avoid re-definition during render
const GrowthIndicator = ({ value }) => {
  if (value > 0) {
    return (
      <span className="flex items-center text-emerald-600 text-sm">
        <ArrowUpRight className="w-4 h-4" />
        +{value}%
      </span>
    );
  } else if (value < 0) {
    return (
      <span className="flex items-center text-red-600 text-sm">
        <ArrowDownRight className="w-4 h-4" />
        {value}%
      </span>
    );
  }
  return <span className="text-slate-400 text-sm">0%</span>;
};

const AnalyticsDashboardPage = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('executive');
  const [period, setPeriod] = useState(30);
  const [executiveData, setExecutiveData] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [missionData, setMissionData] = useState(null);
  const [carrierData, setCarrierData] = useState(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Load executive dashboard
      const execRes = await fetch(`${API_URL}/api/analytics/executive?period_days=${period}`, {
        headers: getHeaders()
      });
      if (execRes.ok) {
        setExecutiveData(await execRes.json());
      }
      
      // Load revenue analytics
      const revRes = await fetch(`${API_URL}/api/analytics/revenue?period_days=${period}`, {
        headers: getHeaders()
      });
      if (revRes.ok) {
        setRevenueData(await revRes.json());
      }
      
      // Load mission analytics
      const missRes = await fetch(`${API_URL}/api/analytics/missions?period_days=${period}`, {
        headers: getHeaders()
      });
      if (missRes.ok) {
        setMissionData(await missRes.json());
      }
      
      // Load carrier analytics
      const carrRes = await fetch(`${API_URL}/api/analytics/carriers?period_days=${period}`, {
        headers: getHeaders()
      });
      if (carrRes.ok) {
        setCarrierData(await carrRes.json());
      }
      
    } catch (error) {
      console.error('Error loading analytics:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const generateReport = async (type) => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/reports/generate?report_type=${type}&period_days=${period}`, {
        method: 'POST',
        headers: getHeaders()
      });
      
      if (res.ok) {
        toast.success(`Rapport ${type} généré avec succès`);
      } else {
        toast.error('Erreur lors de la génération du rapport');
      }
    } catch (error) {
      toast.error('Erreur réseau');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 font-['Chivo'] tracking-tight flex items-center gap-2">
              <BarChart3 className="w-7 h-7 text-orange-500" />
              Business Intelligence
            </h1>
            <p className="text-slate-600 mt-1">Analyses et indicateurs de performance</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <select
              className="border rounded-md px-3 py-2 text-sm flex-1 md:flex-none"
              value={period}
              onChange={(e) => setPeriod(Number(e.target.value))}
            >
              <option value={7}>7 jours</option>
              <option value={30}>30 jours</option>
              <option value={90}>90 jours</option>
              <option value={365}>12 mois</option>
            </select>
            <Button onClick={loadData} variant="outline" size="sm" className="md:size-default">
              <RefreshCw className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Actualiser</span>
            </Button>
          </div>
        </div>

        {/* KPI Cards */}
        {executiveData && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 md:gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">GMV</p>
                    <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900 mt-1">
                      {(executiveData.kpis?.gmv?.current || 0).toLocaleString('fr-FR')}€
                    </p>
                    <GrowthIndicator value={executiveData.kpis?.gmv?.growth} />
                  </div>
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Revenus</p>
                    <p className="text-2xl font-bold font-['JetBrains_Mono'] text-emerald-600 mt-1">
                      {(executiveData.kpis?.revenue?.current || 0).toLocaleString('fr-FR')}€
                    </p>
                    <GrowthIndicator value={executiveData.kpis?.revenue?.growth} />
                  </div>
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Missions</p>
                    <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900 mt-1">
                      {executiveData.kpis?.missions?.current || 0}
                    </p>
                    <GrowthIndicator value={executiveData.kpis?.missions?.growth} />
                  </div>
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Package className="w-5 h-5 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Nouveaux utilisateurs</p>
                    <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900 mt-1">
                      {executiveData.kpis?.new_users?.current || 0}
                    </p>
                    <GrowthIndicator value={executiveData.kpis?.new_users?.growth} />
                  </div>
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Utilisateurs actifs</p>
                    <p className="text-2xl font-bold font-['JetBrains_Mono'] text-slate-900 mt-1">
                      {executiveData.kpis?.active_users?.current || 0}
                    </p>
                    <GrowthIndicator value={executiveData.kpis?.active_users?.growth} />
                  </div>
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 max-w-2xl gap-1">
            <TabsTrigger value="executive" className="text-xs md:text-sm">
              <TrendingUp className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Executive</span>
            </TabsTrigger>
            <TabsTrigger value="revenue" className="text-xs md:text-sm">
              <DollarSign className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Revenus</span>
            </TabsTrigger>
            <TabsTrigger value="missions" className="text-xs md:text-sm">
              <Package className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Missions</span>
            </TabsTrigger>
            <TabsTrigger value="carriers" className="text-xs md:text-sm">
              <Truck className="w-4 h-4 md:mr-2" />
              <span className="hidden md:inline">Transporteurs</span>
            </TabsTrigger>
          </TabsList>

          {/* Executive Tab */}
          <TabsContent value="executive" className="mt-6 space-y-6">
            {executiveData && (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Conversion Metrics */}
                <Card>
                  <CardHeader>
                    <CardTitle>Métriques de conversion</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Taux de complétion missions</span>
                        <span className="font-mono font-bold text-emerald-600">
                          {executiveData.conversion_metrics?.mission_completion_rate || 0}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Taux d&apos;acceptation offres</span>
                        <span className="font-mono font-bold text-blue-600">
                          {executiveData.conversion_metrics?.bid_acceptance_rate || 0}%
                        </span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <span className="text-slate-600">Temps moyen de matching</span>
                        <span className="font-mono font-bold">
                          {executiveData.conversion_metrics?.average_time_to_match || 'N/A'}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Financial Metrics */}
                <Card className="bg-slate-900 text-white">
                  <CardHeader>
                    <CardTitle className="text-white">Métriques financières</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Valeur moyenne mission</span>
                        <span className="font-mono font-bold">
                          {(executiveData.financial_metrics?.average_mission_value || 0).toLocaleString('fr-FR')}€
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Marge moyenne</span>
                        <span className="font-mono font-bold text-emerald-400">
                          {executiveData.financial_metrics?.average_margin || 0}%
                        </span>
                      </div>
                      <hr className="border-slate-700" />
                      <div className="flex items-center justify-between">
                        <span className="text-slate-400">Commissions collectées</span>
                        <span className="font-mono font-bold text-orange-400">
                          {(executiveData.financial_metrics?.platform_fee_collected || 0).toLocaleString('fr-FR')}€
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Revenue Tab */}
          <TabsContent value="revenue" className="mt-6 space-y-6">
            {revenueData && (
              <>
                <div className="grid lg:grid-cols-4 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-slate-500">Volume total</p>
                      <p className="text-2xl font-bold font-mono mt-1">
                        {(revenueData.totals?.gmv || 0).toLocaleString('fr-FR')}€
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-slate-500">Revenus plateforme</p>
                      <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">
                        {(revenueData.totals?.revenue || 0).toLocaleString('fr-FR')}€
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-slate-500">Transactions</p>
                      <p className="text-2xl font-bold font-mono mt-1">
                        {revenueData.totals?.transactions || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-slate-500">Taux de marge</p>
                      <p className="text-2xl font-bold font-mono text-orange-600 mt-1">
                        {revenueData.totals?.margin_rate || 0}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Enterprises */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Entreprises</CardTitle>
                    <CardDescription>Entreprises générant le plus de revenus</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {revenueData.top_enterprises?.length > 0 ? (
                      <div className="space-y-3">
                        {revenueData.top_enterprises.map((enterprise, index) => (
                          <div key={enterprise.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              <span className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-sm font-bold">
                                {index + 1}
                              </span>
                              <div>
                                <p className="font-medium">{enterprise.company_name}</p>
                                <p className="text-sm text-slate-500">{enterprise.transactions} transactions</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="font-mono font-bold">{enterprise.total_spent.toLocaleString('fr-FR')}€</p>
                              <p className="text-sm text-emerald-600">+{enterprise.fees_generated.toLocaleString('fr-FR')}€</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-center text-slate-500 py-8">Aucune donnée disponible</p>
                    )}
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Missions Tab */}
          <TabsContent value="missions" className="mt-6 space-y-6">
            {missionData && (
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Summary */}
                <Card>
                  <CardHeader>
                    <CardTitle>Résumé des missions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <span>Total missions</span>
                        <span className="font-mono font-bold">{missionData.summary?.total_missions || 0}</span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-lg">
                        <span>Taux de complétion</span>
                        <span className="font-mono font-bold text-emerald-600">{missionData.summary?.completion_rate || 0}%</span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                        <span>Taux d&apos;annulation</span>
                        <span className="font-mono font-bold text-red-600">{missionData.summary?.cancellation_rate || 0}%</span>
                      </div>
                      <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                        <span>Offres par mission</span>
                        <span className="font-mono font-bold text-blue-600">{missionData.summary?.avg_bids_per_mission || 0}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Status Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle>Distribution par statut</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {missionData.status_distribution && Object.entries(missionData.status_distribution).map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between p-3 border rounded-lg">
                          <Badge variant="outline" className="capitalize">{status}</Badge>
                          <span className="font-mono font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* By Vehicle Type */}
                <Card className="lg:col-span-2">
                  <CardHeader>
                    <CardTitle>Par type de véhicule</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {missionData.by_vehicle_type && Object.entries(missionData.by_vehicle_type).map(([type, count]) => (
                        <div key={type} className="p-4 bg-slate-50 rounded-lg text-center">
                          <Truck className="w-6 h-6 mx-auto mb-2 text-slate-400" />
                          <p className="text-sm text-slate-600 capitalize">{type}</p>
                          <p className="font-mono font-bold text-xl mt-1">{count}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Carriers Tab */}
          <TabsContent value="carriers" className="mt-6 space-y-6">
            {carrierData && (
              <>
                <div className="grid lg:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-slate-500">Total transporteurs</p>
                      <p className="text-2xl font-bold font-mono mt-1">
                        {carrierData.summary?.total_carriers || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-slate-500">Transporteurs vérifiés</p>
                      <p className="text-2xl font-bold font-mono text-emerald-600 mt-1">
                        {carrierData.summary?.verified_carriers || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-sm text-slate-500">Taux de vérification</p>
                      <p className="text-2xl font-bold font-mono text-blue-600 mt-1">
                        {carrierData.summary?.verification_rate || 0}%
                      </p>
                    </CardContent>
                  </Card>
                </div>

                {/* Top Performers */}
                <Card>
                  <CardHeader>
                    <CardTitle>Top Transporteurs</CardTitle>
                    <CardDescription>Meilleurs transporteurs par missions complétées</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {carrierData.top_performers?.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b text-left">
                              <th className="pb-3 font-medium text-slate-600">#</th>
                              <th className="pb-3 font-medium text-slate-600">Transporteur</th>
                              <th className="pb-3 font-medium text-slate-600">Missions</th>
                              <th className="pb-3 font-medium text-slate-600">Complétion</th>
                              <th className="pb-3 font-medium text-slate-600">Note</th>
                            </tr>
                          </thead>
                          <tbody>
                            {carrierData.top_performers.map((carrier, index) => (
                              <tr key={carrier.id} className="border-b last:border-0">
                                <td className="py-3 font-mono">{index + 1}</td>
                                <td className="py-3 font-medium">{carrier.company_name}</td>
                                <td className="py-3 font-mono">{carrier.completed_missions}/{carrier.total_missions}</td>
                                <td className="py-3">
                                  <Badge className={carrier.completion_rate >= 90 ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                                    {Math.round(carrier.completion_rate)}%
                                  </Badge>
                                </td>
                                <td className="py-3 font-mono">{carrier.avg_rating > 0 ? `${carrier.avg_rating}/5` : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-center text-slate-500 py-8">Aucune donnée disponible</p>
                    )}
                  </CardContent>
                </Card>

                {/* Fleet by Type */}
                <Card>
                  <CardHeader>
                    <CardTitle>Flotte par type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {carrierData.fleet_by_type && Object.entries(carrierData.fleet_by_type).map(([type, data]) => (
                        <div key={type} className="p-4 bg-slate-50 rounded-lg">
                          <p className="text-sm text-slate-600 capitalize">{type}</p>
                          <p className="font-mono font-bold text-xl">{data.total}</p>
                          <p className="text-sm text-emerald-600">{data.available} disponibles</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
        </Tabs>

        {/* Report Generation */}
        <Card className="bg-slate-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Générer un rapport
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" onClick={() => generateReport('revenue')}>
                <Download className="w-4 h-4 mr-2" />
                Rapport Revenus
              </Button>
              <Button variant="outline" onClick={() => generateReport('missions')}>
                <Download className="w-4 h-4 mr-2" />
                Rapport Missions
              </Button>
              <Button variant="outline" onClick={() => generateReport('carriers')}>
                <Download className="w-4 h-4 mr-2" />
                Rapport Transporteurs
              </Button>
              <Button variant="outline" onClick={() => generateReport('compliance')}>
                <Download className="w-4 h-4 mr-2" />
                Rapport Conformité
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AnalyticsDashboardPage;
